import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import VariableOptionList from './VariableOptionList';
import { VariableOption } from './variableOptions';

import styles from './VariableSuggestionPopup.less';

/** The caret's box in **viewport** coordinates — what `coordsAtPos` hands back. */
export interface SuggestionAnchor {
  left: number;
  /** Caret top, for the flipped-above placement. */
  top: number;
  /** Caret bottom, which the list normally hangs from. */
  bottom: number;
}

interface VariableSuggestionPopupProps {
  anchor: SuggestionAnchor | null;
  suggestions: VariableOption[];
  activeIndex: number;
  onPick: (option: VariableOption) => void;
}

/** Breathing room between the caret and the list, and between the list and the edge. */
const CARET_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * The `{{` list for the two editors that have a caret of their own rather than an
 * input to hang a popover on — the Twig source view and the rich-text blocks.
 *
 * Positioned by hand at the coordinates the host reads from its own caret, because
 * neither editor gives the caret a DOM node an antd popup could anchor to. The card
 * itself is the same list as everywhere else.
 *
 * **Portalled to the body, and fixed rather than absolute.** Every surface this opens
 * over clips it otherwise: the rich-text field rounds off its border with
 * `overflow: hidden`, the code field does the same, and the inspector rail scrolls —
 * so a list long enough to be worth reading was cut off at the field's edge, which is
 * exactly when it matters most. Being out of the flow, it also has to keep itself
 * inside the window: it flips above the caret when there is no room below, and clamps
 * to the horizontal edges.
 */
const VariableSuggestionPopup: React.FC<VariableSuggestionPopupProps> = ({
  anchor,
  suggestions,
  activeIndex,
  onPick,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{
    left: number;
    top: number;
  } | null>(null);

  /**
   * Layout, not effect: the card is measured and moved before the browser paints,
   * so it never appears at the unclamped position first.
   */
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || !anchor) {
      setPlacement(null);
      return;
    }

    const { width, height } = card.getBoundingClientRect();
    const below = anchor.bottom + CARET_GAP;
    const above = anchor.top - CARET_GAP - height;
    // Flipping is only an improvement if the list actually fits up there — with no
    // room either way it stays below and is clamped, which at least keeps the first
    // rows (the best matches) on screen.
    const flip =
      below + height > window.innerHeight - VIEWPORT_MARGIN &&
      above >= VIEWPORT_MARGIN;

    setPlacement({
      left: Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchor.left, window.innerWidth - VIEWPORT_MARGIN - width),
      ),
      top: flip
        ? above
        : Math.max(
            VIEWPORT_MARGIN,
            Math.min(below, window.innerHeight - VIEWPORT_MARGIN - height),
          ),
    });
  }, [anchor, suggestions]);

  if (!anchor || suggestions.length === 0) return null;

  return createPortal(
    <div
      ref={cardRef}
      className={styles.popup}
      // The only inline style in the Email Center, and unavoidable: these are
      // caret coordinates measured at runtime, not a design decision.
      style={{
        left: placement?.left ?? anchor.left,
        top: placement?.top ?? anchor.bottom + CARET_GAP,
      }}
      // Hidden for the one frame between mounting and being measured, so the
      // pre-clamp position is never painted.
      data-placed={placement ? '1' : '0'}
      role="listbox"
    >
      <VariableOptionList
        options={suggestions}
        activeIndex={activeIndex}
        onPick={onPick}
      />
    </div>,
    document.body,
  );
};

export default React.memo(VariableSuggestionPopup);
